"use client";

// Wrapped-DEK v2 crypto primitives — must match server/auth_v2.py exactly.
// PBKDF2-HMAC-SHA256 600K, AES-256-GCM with 12-byte nonce prefix,
// 32-char base32 recovery codes in groups of 4 (XXXX-XXXX-...-XXXX).

export const DEK_BYTES = 32;
export const SALT_BYTES = 16;
export const PBKDF2_ITERATIONS = 600_000;
export const AES_GCM_NONCE_BYTES = 12;
export const RECOVERY_CODE_COUNT = 8;
export const RECOVERY_CODE_CHARS = 32;

const PBKDF2_HASH = "SHA-256";

const BASE32_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

// =============================================================================
// Random bytes
// =============================================================================

export function generateDEK(): Uint8Array {
  const buf = new Uint8Array(DEK_BYTES);
  crypto.getRandomValues(buf);
  return buf;
}

export function generateSalt(): Uint8Array {
  const buf = new Uint8Array(SALT_BYTES);
  crypto.getRandomValues(buf);
  return buf;
}

function generateNonce(): Uint8Array {
  const buf = new Uint8Array(AES_GCM_NONCE_BYTES);
  crypto.getRandomValues(buf);
  return buf;
}

// =============================================================================
// Key derivation — returns an AES-GCM CryptoKey usable by encrypt/decrypt.
// =============================================================================

export async function deriveKEK(
  passwordOrCode: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  if (salt.length !== SALT_BYTES) {
    throw new Error(`salt must be ${SALT_BYTES} bytes, got ${salt.length}`);
  }
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passwordOrCode),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH,
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

// =============================================================================
// DEK wrap / unwrap (AES-GCM, nonce prefixed)
// =============================================================================

export async function wrapDEK(
  dek: Uint8Array,
  kek: CryptoKey
): Promise<Uint8Array> {
  if (dek.length !== DEK_BYTES) {
    throw new Error(`dek must be ${DEK_BYTES} bytes`);
  }
  const nonce = generateNonce();
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce.buffer as ArrayBuffer },
      kek,
      dek.buffer as ArrayBuffer
    )
  );
  const out = new Uint8Array(nonce.length + ct.length);
  out.set(nonce, 0);
  out.set(ct, nonce.length);
  return out;
}

export async function unwrapDEK(
  blob: Uint8Array,
  kek: CryptoKey
): Promise<Uint8Array> {
  if (blob.length < AES_GCM_NONCE_BYTES + 16) {
    throw new Error("wrapped_dek blob too short");
  }
  const nonce = blob.slice(0, AES_GCM_NONCE_BYTES);
  const ct = blob.slice(AES_GCM_NONCE_BYTES);
  const pt = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce.buffer as ArrayBuffer },
      kek,
      ct.buffer as ArrayBuffer
    )
  );
  if (pt.length !== DEK_BYTES) {
    throw new Error(`unwrapped DEK has wrong length: ${pt.length}`);
  }
  return pt;
}

// =============================================================================
// Admin pubkey wrap (RSA-OAEP-SHA256)
// =============================================================================

export async function wrapDEKForAdminPubkey(
  dek: Uint8Array,
  pubkeyPEM: string
): Promise<Uint8Array> {
  const spki = pemToSpki(pubkeyPEM);
  const pub = await crypto.subtle.importKey(
    "spki",
    spki.buffer as ArrayBuffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      pub,
      dek.buffer as ArrayBuffer
    )
  );
  return ct;
}

function pemToSpki(pem: string): Uint8Array {
  const body = pem
    .replace(/-----BEGIN [^-]+-----/g, "")
    .replace(/-----END [^-]+-----/g, "")
    .replace(/\s+/g, "");
  return base64ToBytes(body);
}

// =============================================================================
// Recovery codes
// =============================================================================

export function generateRecoveryCode(): string {
  const raw = new Uint8Array(20);
  crypto.getRandomValues(raw);
  const b32 = base32Encode(raw);
  if (b32.length !== RECOVERY_CODE_CHARS) {
    throw new Error(`unexpected b32 length ${b32.length}`);
  }
  const chunks: string[] = [];
  for (let i = 0; i < RECOVERY_CODE_CHARS; i += 4) {
    chunks.push(b32.slice(i, i + 4));
  }
  return chunks.join("-");
}

export function generateRecoveryCodes(count = RECOVERY_CODE_COUNT): string[] {
  return Array.from({ length: count }, () => generateRecoveryCode());
}

export function normalizeRecoveryCode(code: string): string {
  return code.replace(/[-\s]/g, "").toUpperCase();
}

export interface RecoveryPayloadWire {
  code: string;
  recovery_salt: string;
  wrapped_dek: string;
}

export async function buildRecoveryPayloads(
  dek: Uint8Array,
  count = RECOVERY_CODE_COUNT
): Promise<{ plaintext: string[]; wire: RecoveryPayloadWire[] }> {
  const plaintext: string[] = [];
  const wire: RecoveryPayloadWire[] = [];
  for (let i = 0; i < count; i++) {
    const code = generateRecoveryCode();
    const salt = generateSalt();
    const kek = await deriveKEK(normalizeRecoveryCode(code), salt);
    const wrapped = await wrapDEK(dek, kek);
    plaintext.push(code);
    wire.push({
      code,
      recovery_salt: bytesToBase64(salt),
      wrapped_dek: bytesToBase64(wrapped),
    });
  }
  return { plaintext, wire };
}

// =============================================================================
// Base32 (RFC 4648, uppercase, no padding for 20-byte input)
// =============================================================================

function base32Encode(bytes: Uint8Array): string {
  let bits = 0;
  let buffer = 0;
  let out = "";
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      out += BASE32_ALPHABET[(buffer >> bits) & 0x1f];
    }
  }
  if (bits > 0) {
    out += BASE32_ALPHABET[(buffer << (5 - bits)) & 0x1f];
  }
  return out;
}

// =============================================================================
// Base64 helpers
// =============================================================================

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}
